import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { NavController, LoadingController, AlertController } from '@ionic/angular';

import { Address } from "../../models/address";

import { UserService } from '../../services/user-service';
import { BusinessTasks, BusinessLocation } from "../../services/business-tasks";
import { Alert } from "../../services/alert";
import { Util } from "../../services/util";
import { ObservableService } from 'src/app/services/observable-service';

@Component({
  selector: 'app-business-locations',
  templateUrl: './business-locations.page.html',
  styleUrls: ['./business-locations.page.scss'],
})
export class BusinessLocationsPage implements OnInit {

  @ViewChild('addressInput') private addressInput: ElementRef;

  locationFields: {
    name?: string,
  } = {}

  selectedAddress?: Address

  card: any;

  editingCard?: string
  addingLocation = false

  businessLocations: BusinessLocation[]

  constructor(
    private navCtrl: NavController,
    private loadingCtrl: LoadingController,
    private userService: UserService,
    private alert: Alert,
    private changeRef: ChangeDetectorRef,
    private alertCtrl: AlertController,
    private observables: ObservableService,
  ) {
    if (this.userService.user && !this.userService.user.type) {
      this.navCtrl.navigateForward(`/business-change-login`);
    }

    this.pullBusinessLocations()
  }

  ngOnInit() {}

  returnToAccount() {
    this.navCtrl.navigateBack('/business')
  }

  ionViewDidEnter() {
    this.observables.publishSetCurrentMenuRoot('/business')
  }

  private async pullBusinessLocations() {

    let loading = await this.loadingCtrl.create()
    loading.present()

    let onlyIncludeWithStripeAccounts = false;

    BusinessTasks.pullBusinessLocations(onlyIncludeWithStripeAccounts, (businessLocations: BusinessLocation[]) => {

      this.businessLocations = businessLocations
      this.changeRef.detectChanges()
      loading.dismiss();

    }, (err: string) => {
      loading.dismiss();
      if (!Util.isNavigationError(err)) {
        this.error(err)
      }
    })
  }



  addNewLocation() {
    this.addingLocation = true
    this.changeRef.detectChanges()
  }

  closeNewLocation() {
    this.addingLocation = false
    this.changeRef.detectChanges()
  }


  async submitNewLocation() {

    if (!this.locationFields.name) {
      this.error('Please enter location name.')
      return
    }
    if (!this.selectedAddress) {
      this.alert.show('Error', "Please enter your address and select from the dropdown.")
      return
    }

    let loading = await this.loadingCtrl.create()
    loading.present()

    BusinessTasks.addBusinessLocation(this.locationFields.name, this.selectedAddress, (businessLocationID: string) => {

      this.userService.fetch(() => {

        this.businessLocations.push({
          id: businessLocationID,
          name: this.locationFields.name,
          address: this.selectedAddress,
        })

        this.locationFields.name = "";
        const addressInput = this.addressInput.nativeElement as HTMLInputElement;
        addressInput.value = '';
        this.selectedAddress = undefined;

        this.changeRef.detectChanges()

        loading.dismiss();

      }, (err: string) => {
        loading.dismiss();
        this.error(err)
      })

    }, (err: string) => {

      loading.dismiss();
      this.error(err)

    })
  }

  async setUpStripe(businessLocationID: string) {

    if (this.businessLocations.length < 2) {
      this.openSetupStripeLink(businessLocationID)
      return
    }

    const alert = await this.alertCtrl.create({
      header: 'Stripe: Using multiple bank accounts',
      message: 'If you require distinct bank accounts per location, use a <b>+</b> symbol for new Stripe accounts with email variations, such as <b>email+loc1@ex.com</b> and <b>email+loc2@ex.com</b>. Note: email@ex.com will still receive the notifications.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          handler: (blah) => {
            // Nothing
          }
        }, {
          text: 'Continue',
          handler: () => {
            this.openSetupStripeLink(businessLocationID)
          }
        }
      ]
    });

    await alert.present();
  }

  async useExistingStripeAccount(businessLocationID: string) {

    let businessLocationName = 'Unknown';
    let inputs = [];
    for (let businessLocation of this.businessLocations) {
      if (businessLocation.stripeAccountID) {
        // Only show locations with stripe accounts.
        inputs.push({
          type: 'radio',
          label: `${businessLocation.name}: Stripe ID ${businessLocation.stripeAccountID}`,
          value: businessLocation.stripeAccountID,
        })
      }
      if (businessLocation.id === businessLocationID) {
        businessLocationName = businessLocation.name;
      }
    }

    const alert = await this.alertCtrl.create({
      header: 'Select Stripe account',
      message: `We'll copy your Stripe account's settings to this location: ${businessLocationName}.`,
      inputs: inputs,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
          handler: (blah) => {
            // Nothing
          }
        }, {
          text: 'Select',
          handler: stripeAccountID => {

            if (!stripeAccountID) {
              return;
            }

            this.setExistingStripeAccount(businessLocationID, stripeAccountID)
          }
        }
      ]
    });

    await alert.present();
  }

  private async setExistingStripeAccount(businessLocationID: string, stripeAccountID: string) {

    let loading = await this.loadingCtrl.create()
    loading.present()

    BusinessTasks.useExistingStripeAccountForBusinessLocation(businessLocationID, stripeAccountID,
    () => {
      loading.dismiss();

      for (let businessLocation of this.businessLocations) {
        if (businessLocation.id === businessLocationID) {
          businessLocation.stripeAccountID = stripeAccountID;
        }
      }

      this.changeRef.detectChanges();

    }, (errorMsg: string) => {
        loading.dismiss()
        this.alert.show('Oops!', `Something went wrong. \n\n${errorMsg}`)
    })
  }

  private async openSetupStripeLink(businessLocationID: string) {

    let loading = await this.loadingCtrl.create()
    loading.present()

    BusinessTasks.generateStripeRegisterURL(businessLocationID,
    (url: string) => {
      loading.dismiss();

      this.openPage(url)

    }, (errorMsg: string) => {
        loading.dismiss()
        this.alert.show('Oops!', `Something went wrong. \n\n${errorMsg}`)
    })
  }

  private openPage(url: string) {
    window.open(url, '_blank')
  }

  async viewStripe(businessLocationID: string) {

    let loading = await this.loadingCtrl.create()
    loading.present()

    BusinessTasks.getStripeExpressLoginLink_Business(businessLocationID, (url: string) => {

      loading.dismiss();
      this.openPage(url)

    }, (err: string) => {

      loading.dismiss();
      console.error(err)

    })
  }

  hasExistingStripeAccounts() {
    for (const location of this.businessLocations) {
      if (location.stripeAccountID) {
        return true;
      }
    }
    return false;
  }


  handleAddressChange(place: any) {

    let components = place.address_components;
    let streetNumber = this.extractFromAddress(components, 'street_number')
    let streetName = this.extractFromAddress(components, 'route')
    var city = this.extractFromAddress(components, 'locality')
    let state = this.extractFromAddress(components, 'administrative_area_level_1')
    let zip = this.extractFromAddress(components, 'postal_code')
    let country = this.extractFromAddress(components, 'country')

    if (!city) {
      // If city wasn't found under "locality", search for city name under "sublocality"
      city = this.extractFromAddress(components, 'sublocality')
    }
    if (!city) {
      // If city wasn't found under "locality" or "sublocality"
      city = this.extractFromAddress(components, 'postal_town')
    }
    if (!city) {
      // If city wasn't found under "postal_town"
      city = this.extractFromAddress(components, 'administrative_area_level_3')
    }

    // console.log(components)
    // console.log(streetName)
    // console.log(city)
    // console.log(state)
    // console.log(zip)
    // console.log(country)

    if (!streetName || !city || !state || !zip) {
      // console.log(components)
      this.alert.show('Error', "Selection invalid. Please choose a valid U.S. or U.K. address with a zip code.")
      return
    }
    if (zip.length < 2) {
      this.alert.show('Error', "Please select an address with a zip code.")
      return
    }

    var address = {
      street: streetName,
      city: city,
      state: state,
      zip: zip,
      country: country,
      coordinates: {
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      }
    }

    if (streetNumber) {
      address.street = `${streetNumber} ${streetName}`
      console.log(address)
      this.selectedAddress = address
    } else {
      this.alert.showPrompt('Please re-enter street number', null, 'streetNumber', 'Street number', 'number', 'Save', (data) => {
        let newStreetNumber = data.streetNumber
        if (newStreetNumber && newStreetNumber != '') {
          address.street = `${newStreetNumber} ${streetName}`
          console.log(address)
          this.selectedAddress = address
        } else {
          this.alert.show('Error', 'Please provide a street number.')
        }
      })
    }
  }

  private extractFromAddress(components: any[], type: string) {
    for (var i = 0; i < components.length; i++) {
      for (var j = 0; j < components[i].types.length; j++) {
        if (components[i].types[j] == type) return components[i].short_name;
      }
    }

    return undefined;
  }



  private error(msg: string) {
    this.alert.show('Error', msg)
  }


}
